import '@/logic/telemetry.ts'
import { Auth0Provider } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import i18next from 'i18next'
import { type ReactNode, StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'
import 'tailwindcss/tailwind.css'
import translations from '../src/i18n/locales/translations.json'
import App from './App.tsx'
import auth0Config from './config/auth0.config'
import './index.scss'
import { TrackingProvider } from './providers/TrackingProvider.tsx'
import { ToastProvider } from './shared/hooks/ToastProvider.tsx'

void i18next.init({
  lng: 'en',
  fallbackLng: 'en',
  interpolation: {
    escapeValue: true,
  },
  resources: Object.fromEntries(
    Object.entries(translations).map(([lang, translation]) => [
      lang,
      { translation },
    ])
  ),
})

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnMount: 'always',
      refetchOnReconnect: true,
      staleTime: 1000 * 60 * 10,
      gcTime: Number.POSITIVE_INFINITY,
    },
  },
})

function Main(): ReactNode {
  return (
    <I18nextProvider i18n={i18next}>
      <TrackingProvider>
        <ToastProvider>
          <Auth0Provider
            domain={auth0Config.domain}
            clientId={auth0Config.clientId}
            useRefreshTokens={true}
            cacheLocation="localstorage"
            authorizationParams={{
              redirect_uri: `${window.location.origin}${window.location.pathname}`,
            }}
            onRedirectCallback={(appState) => {
              if (appState && appState.returnTo) {
                window.history.replaceState({}, '', appState.returnTo)
              }
            }}
          >
            <QueryClientProvider client={queryClient}>
              <App />
            </QueryClientProvider>
          </Auth0Provider>
        </ToastProvider>
      </TrackingProvider>
    </I18nextProvider>
  )
}

const elm = document.getElementById('root') as HTMLElement
createRoot(elm).render(
  <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
    <StrictMode>
      <Main />
    </StrictMode>
  </Sentry.ErrorBoundary>
)
